import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

declare global {
  interface Window {
    Sentry?: {
      captureException: (err: unknown) => string;
      lastEventId: () => string | undefined;
    };
  }
}

interface Props {
  children: ReactNode;
  /**
   * Human-readable label shown in the fallback ("Abonnement", "Beheerconsole", …).
   * When omitted the boundary acts as the global root boundary ("Iets ging mis").
   */
  scope?: string;
}

interface State {
  error: Error | null;
  sentryEventId: string | null;
}

/**
 * Defense-in-depth boundary.
 *
 * Two-tier usage:
 *   1. Root (no scope) — wraps <App /> / <StageryRouter /> in index.tsx.
 *      Shows a branded full-page "Iets ging mis" screen with Sentry event ID
 *      and contact mailto. Has role="alert" aria-live="assertive" for a11y.
 *   2. Scoped (scope="Beheerconsole") — nested boundary for specific views.
 *      Catches inner crashes before they bubble to root; shows scope-specific label.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, sentryEventId: null };

  static getDerivedStateFromError(error: Error): Pick<State, 'error'> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.scope ?? 'root', error, info.componentStack);

    let eventId: string | null = null;
    try {
      if (window.Sentry) {
        eventId = window.Sentry.captureException(error) ?? window.Sentry.lastEventId() ?? null;
      }
    } catch {
      // Sentry not available — swallow
    }
    this.setState({ sentryEventId: eventId });
  }

  handleRetry = () => {
    this.setState({ error: null, sentryEventId: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { scope } = this.props;
    const { sentryEventId } = this.state;
    const message = this.state.error.message || 'Onbekende fout';
    const isRoot = !scope;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-screen bg-bg flex items-center justify-center p-8"
      >
        <div className="max-w-md w-full bg-white border border-black/[0.08] rounded-2xl p-8 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.12)] text-center">
          {isRoot && (
            <div className="mb-6">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-ink text-white font-bold text-xl">S</span>
            </div>
          )}
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={22} aria-hidden="true" />
          </div>
          <h2 className="font-sans text-xl font-bold text-ink mb-2">
            {isRoot ? 'Iets ging mis' : `Er ging iets mis op ${scope}`}
          </h2>
          <p className="text-sm text-ink/60 mb-6 leading-relaxed">
            We konden {isRoot ? 'Stagery' : 'deze pagina'} niet laden. Probeer het opnieuw, en als dit blijft
            gebeuren{' '}
            <a
              href="mailto:support@stagery.ai"
              className="underline underline-offset-2 hover:text-ink"
            >
              neem contact op
            </a>
            .
          </p>
          <pre className="text-[11px] text-ink/50 bg-black/[0.03] rounded-lg p-3 mb-5 text-left overflow-auto max-h-32 font-mono">
            {message}
          </pre>
          {sentryEventId && (
            <p className="text-[10px] text-ink/35 mb-4 font-mono">
              Foutmelding ID: {sentryEventId}
            </p>
          )}
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center gap-1.5 bg-ink text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-accent hover:text-ink transition-colors"
            >
              <RotateCcw size={14} aria-hidden="true" /> Opnieuw proberen
            </button>
            <a
              href="/"
              className="inline-flex items-center gap-1.5 border border-black/10 hover:border-black/20 px-4 py-2.5 rounded-lg text-sm font-semibold text-ink/75 hover:text-ink transition-colors"
            >
              Naar overzicht
            </a>
          </div>
        </div>
      </div>
    );
  }
}
